import { useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { trademoTrackingEvents } from "@core/global/trademo-tracking";
import { generateTrackingObject } from "@core/global/global-country-functions";
import { getUserActivePlan } from "@helpers/profileHelper";
import trackingEventDictionary from "@core/dictionary/tracking-event";
import hermesSparkleIcon from "@assets/images/hermes_floating_sparkle.svg";
import { BRAND } from "@core/brand/brand-config";

const BUTTON_SIZE = 48;
const EDGE_MARGIN = 12;
const TOP_MARGIN = 54;
const DRAG_THRESHOLD = 5;
const POSITION_STORAGE_KEY = "hermesFloatingButtonY";

const getDefaultY = () => window.innerHeight - BUTTON_SIZE - EDGE_MARGIN;

const clampY = (y) => Math.min(Math.max(y, TOP_MARGIN), window.innerHeight - BUTTON_SIZE - 8);

const readSavedY = () => {
  const saved = Number(localStorage.getItem(POSITION_STORAGE_KEY));
  return Number.isFinite(saved) && saved > 0 ? clampY(saved) : getDefaultY();
};

function HermesFloatingButton() {
  const history = useHistory();
  const { pathname } = useLocation();
  const dragInfo = useRef({ startY: 0, offsetY: 0, moved: false });
  const [y, setY] = useState(readSavedY);
  const [isDragging, setIsDragging] = useState(false);

  useEffect(() => {
    const handleResize = () => setY((prev) => clampY(prev));
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handlePointerMove = useCallback((e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    if (e.cancelable) e.preventDefault();
    if (!dragInfo.current.moved && Math.abs(clientY - dragInfo.current.startY) > DRAG_THRESHOLD) {
      dragInfo.current.moved = true;
    }
    setY(clampY(clientY - dragInfo.current.offsetY));
  }, []);

  const handlePointerUp = useCallback(() => {
    setIsDragging(false);
    setY((prev) => {
      localStorage.setItem(POSITION_STORAGE_KEY, String(prev));
      return prev;
    });
    window.removeEventListener("mousemove", handlePointerMove);
    window.removeEventListener("mouseup", handlePointerUp);
    window.removeEventListener("touchmove", handlePointerMove);
    window.removeEventListener("touchend", handlePointerUp);
  }, [handlePointerMove]);

  const handlePointerDown = (e) => {
    const clientY = e.touches ? e.touches[0].clientY : e.clientY;
    dragInfo.current = { startY: clientY, offsetY: clientY - y, moved: false };
    setIsDragging(true);
    window.addEventListener("mousemove", handlePointerMove);
    window.addEventListener("mouseup", handlePointerUp);
    window.addEventListener("touchmove", handlePointerMove, { passive: false });
    window.addEventListener("touchend", handlePointerUp);
  };

  if (pathname === "/hermes-chat" || pathname.startsWith("/hermes-chat/")) return null;

  const handleClick = () => {
    if (dragInfo.current.moved) return;
    trademoTrackingEvents(trackingEventDictionary.hermesFloatingButtonClick, generateTrackingObject("hermes-floating-button", pathname, getUserActivePlan()));
    const currentParams = new URLSearchParams(history.location.search);
    const hermesParams = new URLSearchParams();
    if (currentParams.get("country")) hermesParams.set("country", currentParams.get("country"));
    if (currentParams.get("type")) hermesParams.set("type", currentParams.get("type"));
    history.push(`/hermes-chat${hermesParams.toString() ? `?${hermesParams.toString()}` : ""}`);
  };

  return (
    <div className="tw-group tw-fixed tw-z-[1000]" style={{ top: y, right: EDGE_MARGIN }}>
      <div className={`tw-absolute tw-bottom-full tw-right-0 tw-mb-2 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-gray-800 tw-rounded-lg tw-whitespace-nowrap tw-opacity-0 tw-transition-opacity tw-delay-0 tw-pointer-events-none ${isDragging ? "" : "group-hover:tw-opacity-100 group-hover:tw-delay-[2000ms]"}`}>Drag to move</div>
      <button
        type="button"
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        onClick={handleClick}
        style={{
          borderRadius: "1rem",
          border: "1px solid var(--Gray-2, #EAECF0)",
          background: "var(--Hermes_grad, linear-gradient(314deg, #3647D8 0.96%, #738AF3 30.67%, #A381F8 52.83%, #C57BFB 69.81%, #CDC8E9 93.86%))",
          boxShadow: "0 8px 16px 0 rgba(0, 0, 0, 0.10)",
        }}
        className={`tw-flex tw-items-center tw-justify-start tw-float-right tw-select-none tw-text-white tw-h-12 tw-w-12 ${BRAND.key === "source_atlas" ? "group-hover:tw-w-52" : "group-hover:tw-w-40"} tw-px-[14px] tw-overflow-hidden tw-ease-in-out ${isDragging ? "tw-cursor-grabbing tw-transition-none" : "tw-cursor-grab tw-transition-all tw-duration-300"}`}>
        <img src={hermesSparkleIcon} alt="" className="tw-h-[18px] tw-w-[18px] tw-shrink-0 tw-pointer-events-none" draggable={false} />
        <span className={`tw-max-w-0 ${BRAND.key === "source_atlas" ? "group-hover:tw-max-w-[170px]" : "group-hover:tw-max-w-[110px]"} tw-overflow-hidden tw-whitespace-nowrap tw-opacity-0 group-hover:tw-opacity-100 tw-ml-0 group-hover:tw-ml-2 tw-text-sm tw-font-semibold tw-transition-all tw-duration-300 tw-ease-in-out`}>Ask {BRAND.aiAssistantName}</span>
      </button>
    </div>
  );
}

export default HermesFloatingButton;
